import React, { useEffect, useMemo, useState } from 'react';
import {
  FormControlLabel,
  Checkbox,
  IconButton,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import TipTapEditor from '../TipTapEditor/TipTapEditor';
import AutocompleteSelectFilter from '../AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import EnumsService from '../../../Services/enums.service';
import Constants from '../../../assests/Constants/constants';
import { emptyRule } from './ocrRoutingRulesUtils';
import styles from './AddAccountCardModal.module.scss';

const NO_OVERRIDE = '__none__';
const AUTO_INHERIT = 'inherit';
const AUTO_YES = 'yes';
const AUTO_NO = 'no';
const FILTER_Z = 10150;

const mapCardToOption = (card, t) => ({
  value: String(card.cardId ?? card.value ?? ''),
  label: `${card.cardId ?? card.value ?? ''} - ${card.name ?? card.label ?? ''}`,
  group: card.palCode || t('NonPalCode'),
  card,
});

const FilterSelect = ({ direction, groupBy, ...props }) => (
  <AutocompleteSelectFilter
    isMultiple={false}
    direction={direction}
    portalMenu
    searchable
    portalZIndex={FILTER_Z}
    groupBy={groupBy}
    {...props}
  />
);

const OcrRoutingRulesEditor = ({
  value,
  onChange,
  disabled = false,
  direction = 'rtl',
  defaultTarget = 'debit',
  caseId,
  token,
  accountCardOptions = [],
  sortCodeOptions = [],
}) => {
  const { t } = useTranslation();
  const config = value ?? { enabled: false, rules: [] };
  const [fetchedAccountCards, setFetchedAccountCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const lockedTarget = defaultTarget === 'credit' ? 'credit' : 'debit';

  useEffect(() => {
    if (!config.enabled || !caseId || !token) {
      setFetchedAccountCards([]);
      return undefined;
    }

    let cancelled = false;

    const loadCards = async () => {
      setCardsLoading(true);
      try {
        const response = await EnumsService.GetAccountCardsByCaseId(caseId, token);
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled && Array.isArray(data)) {
          setFetchedAccountCards(data);
        }
      } catch {
        if (!cancelled) setFetchedAccountCards([]);
      } finally {
        if (!cancelled) setCardsLoading(false);
      }
    };

    loadCards();
    return () => {
      cancelled = true;
    };
  }, [config.enabled, caseId, token]);

  const accountCardAutocompleteOptions = useMemo(() => {
    const merged = new Map();
    (accountCardOptions || []).forEach((card) => {
      const option = mapCardToOption(card, t);
      if (option.value) merged.set(option.value, option);
    });
    fetchedAccountCards.forEach((card) => {
      const option = mapCardToOption(card, t);
      if (option.value) merged.set(option.value, option);
    });
    return Array.from(merged.values());
  }, [accountCardOptions, fetchedAccountCards, t]);

  const vatOptions = useMemo(
    () => [
      { value: NO_OVERRIDE, label: t('OcrRoutingVatKeepDefault') },
      ...Constants.VATPercentage.map((option) => ({
        value: String(option),
        label: option === 16 ? t('AccountingTax') : `${option}%`,
      })),
    ],
    [t]
  );

  const autoProcessOptions = useMemo(
    () => [
      { value: AUTO_INHERIT, label: t('OcrRoutingAutoProcessInherit') },
      { value: AUTO_YES, label: t('OcrRoutingAutoProcessYes') },
      { value: AUTO_NO, label: t('OcrRoutingAutoProcessNo') },
    ],
    [t]
  );

  const sortCodeAutocompleteOptions = useMemo(
    () => [
      { value: NO_OVERRIDE, label: t('OcrRoutingSortCodeKeepDefault') },
      ...(sortCodeOptions || []).map((option) => ({
        value: String(option.value ?? option.sortCodeId ?? option.SortCodeId ?? ''),
        label: option.label || String(option.value ?? option.sortCodeId ?? ''),
      })).filter((o) => o.value && o.value !== NO_OVERRIDE),
    ],
    [sortCodeOptions, t]
  );

  const normalizeRuleTarget = (rule) =>
    lockedTarget ? { ...rule, target: lockedTarget } : rule;

  const setEnabled = (enabled) => {
    onChange({
      ...config,
      enabled,
      rules: enabled && (!config.rules || config.rules.length === 0)
        ? [emptyRule(lockedTarget)]
        : (config.rules ?? []).map(normalizeRuleTarget),
    });
  };

  const updateRule = (id, patch) => {
    onChange({
      ...config,
      rules: (config.rules ?? []).map((r) =>
        r.id === id ? normalizeRuleTarget({ ...r, ...patch }) : normalizeRuleTarget(r)
      ),
    });
  };

  const removeRule = (id) => {
    onChange({
      ...config,
      rules: (config.rules ?? []).filter((r) => r.id !== id),
    });
  };

  const addRule = () => {
    onChange({
      ...config,
      rules: [...(config.rules ?? []), emptyRule(lockedTarget)],
    });
  };

  const resolveSelectedCardOption = (accountCardId) => {
    if (!accountCardId) return null;
    const id = String(accountCardId);
    return accountCardAutocompleteOptions.find((o) => o.value === id) ?? null;
  };

  const resolveVatOption = (vatPercentage) => {
    if (vatPercentage == null) {
      return vatOptions.find((o) => o.value === NO_OVERRIDE) ?? vatOptions[0] ?? null;
    }
    return (
      vatOptions.find(
        (o) => o.value !== NO_OVERRIDE && Number(o.value) === Number(vatPercentage)
      ) ?? vatOptions[0] ?? null
    );
  };

  const resolveAutoProcessOption = (autoProcess) => {
    if (autoProcess === true) return autoProcessOptions.find((o) => o.value === AUTO_YES) ?? null;
    if (autoProcess === false) return autoProcessOptions.find((o) => o.value === AUTO_NO) ?? null;
    return autoProcessOptions.find((o) => o.value === AUTO_INHERIT) ?? autoProcessOptions[0] ?? null;
  };

  const resolveSortCodeOption = (sortCodeId) => {
    if (!sortCodeId) {
      return sortCodeAutocompleteOptions.find((o) => o.value === NO_OVERRIDE) ?? sortCodeAutocompleteOptions[0] ?? null;
    }
    return (
      sortCodeAutocompleteOptions.find((o) => o.value === String(sortCodeId)) ??
      sortCodeAutocompleteOptions.find((o) => o.value === NO_OVERRIDE) ??
      null
    );
  };

  const renderField = (label, content) => (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {content}
    </div>
  );

  return (
    <div className={styles.ocrWrap}>
      <FormControlLabel
        control={
          <Checkbox
            checked={!!config.enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={disabled}
          />
        }
        label={t('OcrRoutingEnable')}
      />

      {config.enabled && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          <p className={styles.ocrHint}>{t('OcrRoutingIfElseHint')}</p>
          <p className={styles.ocrHint}>{t('OcrRoutingConditionsHelper')}</p>

          {cardsLoading && accountCardAutocompleteOptions.length === 0 && (
            <div className={styles.loadingRow}>
              <CircularProgress size={18} />
              <span>{t('LoadingAccountCards')}</span>
            </div>
          )}

          {(config.rules ?? []).map((rule, index) => (
            <div key={rule.id} className={styles.ruleCard}>
              <div className={styles.ruleHeader}>
                <div>
                  <div className={styles.ruleTitle}>
                    {t('OcrRoutingRuleNumber', { number: index + 1 })}
                  </div>
                  <div className={styles.ruleMeta}>
                    {t('OcrRoutingTargetLabel')}:{' '}
                    {lockedTarget === 'credit'
                      ? t('OcrRoutingTargetCredit')
                      : t('OcrRoutingTargetDebit')}
                  </div>
                </div>
                <IconButton
                  size="small"
                  className={styles.deleteBtn}
                  disabled={disabled || (config.rules?.length ?? 0) <= 1}
                  onClick={() => removeRule(rule.id)}
                  aria-label={t('Delete')}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </div>

              <div className={styles.editorWrap}>
                <TipTapEditor
                  value={rule.content || ''}
                  onChange={(html) => updateRule(rule.id, { content: html })}
                  placeholder={t('OcrRoutingRulePlaceholder')}
                  direction={direction}
                  editable={!disabled}
                />
              </div>

              <div className={styles.fieldsGrid}>
                {renderField(
                  t('OcrRoutingAccountCardLabel'),
                  <FilterSelect
                    direction={direction}
                    options={accountCardAutocompleteOptions}
                    selectedValues={resolveSelectedCardOption(rule.accountCardId)}
                    onChange={(_, v) => updateRule(rule.id, { accountCardId: v?.value ?? '' })}
                    placeholder={t('OcrRoutingSelectAccountCard')}
                    isDisabled={disabled}
                    groupBy={(option) => option.group}
                  />
                )}

                {renderField(
                  t('OcrRoutingSortCodeLabel'),
                  <FilterSelect
                    direction={direction}
                    options={sortCodeAutocompleteOptions}
                    selectedValues={resolveSortCodeOption(rule.sortCodeId)}
                    onChange={(_, v) => {
                      if (!v || v.value === NO_OVERRIDE) {
                        updateRule(rule.id, { sortCodeId: '' });
                        return;
                      }
                      updateRule(rule.id, { sortCodeId: String(v.value) });
                    }}
                    placeholder={t('OcrRoutingSelectSortCode')}
                    isDisabled={disabled}
                  />
                )}

                {renderField(
                  t('OcrRoutingVatLabel'),
                  <FilterSelect
                    direction={direction}
                    options={vatOptions}
                    selectedValues={resolveVatOption(rule.vatPercentage)}
                    onChange={(_, v) => {
                      if (!v || v.value === NO_OVERRIDE) {
                        updateRule(rule.id, { vatPercentage: null });
                        return;
                      }
                      updateRule(rule.id, { vatPercentage: Number(v.value) });
                    }}
                    placeholder={t('OcrRoutingVatLabel')}
                    isDisabled={disabled}
                  />
                )}

                {renderField(
                  t('OcrRoutingAutoProcessLabel'),
                  <FilterSelect
                    direction={direction}
                    options={autoProcessOptions}
                    selectedValues={resolveAutoProcessOption(rule.autoProcess)}
                    onChange={(_, v) => {
                      const selected = v?.value ?? AUTO_INHERIT;
                      updateRule(rule.id, {
                        autoProcess:
                          selected === AUTO_YES ? true : selected === AUTO_NO ? false : null,
                      });
                    }}
                    placeholder={t('OcrRoutingAutoProcessLabel')}
                    isDisabled={disabled}
                  />
                )}
              </div>
            </div>
          ))}

          <button
            type="button"
            className={`${styles.secondaryBtn} ${styles.compactBtn}`}
            onClick={addRule}
            disabled={disabled}
          >
            <AddIcon sx={{ fontSize: 16 }} />
            {t('OcrRoutingAddRule')}
          </button>
        </div>
      )}
    </div>
  );
};

export default OcrRoutingRulesEditor;
